import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  Sparkles, X, Loader2, Send, Car, DollarSign, CalendarDays,
  AlertTriangle, TrendingUp, RefreshCw, MessageSquare,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * FlowIA — operative AI helper.
 * - Floating button bottom-left
 * - Slide-in panel with KPI cards + suggested questions + free-text question
 * - Backed by /api/flow-ia/summary, /suggestions, /ask (deterministic, real data)
 */
export default function FlowIA() {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState([]); // array of {q, title, lines}
  const [asking, setAsking] = useState(false);
  const inputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        axios.get(`${API}/flow-ia/summary`, { withCredentials: true }),
        axios.get(`${API}/flow-ia/suggestions`, { withCredentials: true }),
      ]);
      setSummary(s.data);
      setSuggestions(q.data?.questions || []);
    } catch (err) {
      // silent — user might not be authenticated yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && !summary) loadAll();
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh while panel open
  useEffect(() => {
    if (!open) return;
    const id = setInterval(loadAll, 30000);
    return () => clearInterval(id);
  }, [open]);

  const ask = async (text) => {
    const q = (text || question || '').trim();
    if (!q) return;
    setAsking(true);
    try {
      const { data } = await axios.post(
        `${API}/flow-ia/ask`,
        { question: q },
        { withCredentials: true },
      );
      setAnswers((prev) => [{ q, title: data.title, lines: data.lines || [], intent: data.intent }, ...prev].slice(0, 8));
      setSummary(data.data || summary);
      setQuestion('');
    } catch {
      setAnswers((prev) => [{ q, title: 'No pude responder', lines: ['Probá otra pregunta o reintenta.'], intent: 'error' }, ...prev]);
    } finally {
      setAsking(false);
    }
  };

  const fmt = (n) => {
    try { return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0); }
    catch { return `$${n || 0}`; }
  };

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Adentro',          value: summary.vehicles_inside,             icon: Car,            tone: 'sky',     hint: `${summary.members_inside} socios · ${summary.visitors_inside} visitantes` },
      { label: 'Sin cobrar',       value: summary.vehicles_pending_pay,        icon: AlertTriangle,  tone: 'amber',   hint: 'Pendientes adentro' },
      { label: 'Cobrado hoy',      value: fmt(summary.revenue_today),          icon: DollarSign,     tone: 'emerald', hint: `${summary.completed_today} salidas` },
      { label: 'Estacionamiento',  value: fmt(summary.parking_revenue_today),  icon: Car,            tone: 'blue',    hint: `${summary.parking_ops_today} ops` },
      { label: 'Eventos',          value: fmt(summary.event_revenue_today),    icon: CalendarDays,   tone: 'amber',   hint: `${summary.event_ops_today} tickets/addons` },
      { label: 'Operaciones',      value: summary.total_entries_today,         icon: TrendingUp,     tone: 'violet',  hint: `Promedio ${Math.round(summary.avg_duration_minutes_today || 0)} min` },
    ];
  }, [summary]);

  const toneCls = {
    sky:     'bg-sky-500/10 border-sky-500/30 text-sky-300',
    amber:   'bg-amber-500/10 border-amber-500/30 text-amber-300',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    blue:    'bg-blue-500/10 border-blue-500/30 text-blue-300',
    violet:  'bg-violet-500/10 border-violet-500/30 text-violet-300',
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir Flow IA"
          data-testid="flow-ia-fab"
          className="fixed z-40 bottom-5 left-5 flex items-center gap-2 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-900/30 px-4 py-3 hover:scale-105 transition-transform border border-emerald-300/30"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-bold tracking-wide">Flow IA</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="flow-ia-panel"
          className="fixed z-50 bottom-3 left-3 right-3 sm:right-auto sm:bottom-5 sm:left-5 w-auto sm:w-[420px] max-h-[85vh] flex flex-col rounded-2xl border border-emerald-500/30 bg-slate-900/95 backdrop-blur-md text-slate-100 shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
            <div className="flex items-center gap-2">
              <span className="relative">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                <span className="absolute -inset-1 rounded-full bg-emerald-400/20 blur-sm" />
              </span>
              <div>
                <p className="text-sm font-bold leading-tight">Flow IA</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 leading-tight">Asistente operativo</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={loadAll}
                disabled={loading}
                aria-label="Refrescar"
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Cerrar"
                data-testid="flow-ia-close"
                className="p-1.5 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto px-3 py-3 space-y-3">
            {/* KPI grid */}
            <div className="grid grid-cols-2 gap-2" data-testid="flow-ia-kpis">
              {kpis.length === 0 && loading && (
                <div className="col-span-2 flex items-center justify-center py-8 text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando…
                </div>
              )}
              {kpis.map((k) => {
                const Icon = k.icon;
                return (
                  <div key={k.label} className={`rounded-lg border p-2.5 ${toneCls[k.tone]}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] uppercase tracking-wide opacity-80">{k.label}</span>
                      <Icon className="w-3.5 h-3.5 opacity-80" />
                    </div>
                    <div className="text-lg font-bold text-white truncate">{k.value}</div>
                    <div className="text-[10px] text-slate-400 truncate">{k.hint}</div>
                  </div>
                );
              })}
            </div>

            {/* Top events */}
            {summary?.top_events_today?.length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-amber-300 mb-1.5 flex items-center gap-1">
                  <CalendarDays className="w-3.5 h-3.5" />Top eventos hoy
                </p>
                <div className="space-y-1">
                  {summary.top_events_today.slice(0, 3).map((e) => (
                    <div key={e.name} className="flex items-center justify-between text-xs">
                      <span className="text-slate-200 truncate mr-2">{e.name}</span>
                      <span className="text-emerald-400 font-mono">{fmt(e.revenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Alerts */}
            {summary?.long_stay_alerts?.length > 0 && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-2.5">
                <p className="text-[10px] uppercase tracking-wide text-red-300 mb-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />Vehiculos hace +8h
                </p>
                <div className="space-y-1">
                  {summary.long_stay_alerts.slice(0, 3).map((a) => (
                    <div key={a.plate} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-white">{a.plate}</span>
                      <span className="text-slate-400">{Math.floor(a.minutes_inside / 60)}h {a.minutes_inside % 60}m</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Answers thread */}
            {answers.length > 0 && (
              <div className="space-y-2" data-testid="flow-ia-answers">
                {answers.map((a, i) => (
                  <div key={i} className="rounded-lg border border-emerald-500/20 bg-slate-800/60 p-2.5">
                    <p className="text-[11px] text-slate-400 flex items-center gap-1 mb-1">
                      <MessageSquare className="w-3 h-3" />{a.q}
                    </p>
                    <p className="text-sm font-bold text-white">{a.title}</p>
                    <ul className="mt-1 space-y-0.5">
                      {a.lines.map((line, idx) => (
                        <li key={idx} className="text-xs text-slate-300">{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            {/* Suggested questions */}
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    onClick={() => ask(q.label)}
                    disabled={asking}
                    data-testid={`flow-ia-suggest-${q.id}`}
                    className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 bg-slate-800 text-slate-300 hover:bg-emerald-600 hover:text-white hover:border-emerald-500 transition-colors disabled:opacity-50"
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Ask input */}
          <form
            onSubmit={(e) => { e.preventDefault(); ask(); }}
            className="border-t border-slate-700/60 p-2 flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Pregunta operativa, ej: ¿cuántos hay adentro?"
              data-testid="flow-ia-input"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            <button
              type="submit"
              disabled={asking || !question.trim()}
              data-testid="flow-ia-send"
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white p-2"
              aria-label="Enviar"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
